<template>
  <div
    class="popup-holder popup-share"
    :style="{ display: showSharePopup ? 'flex' : 'none' }"
  >
    <div class="popup-wrap">
      <div class="popup-head">
        <h2>Share "{{ getMediaTitle }}"</h2>
        <a @click="closeSharePopup" href="#" class="popup-close icon-close"></a>
      </div>
      <div class="popup-body">
        <!-- Loading State -->
        <div v-if="loading" class="loading-state">
          <div class="loading-spinner"></div>
          <p>{{ loadingMessage }}</p>
        </div>

        <!-- Error State -->
        <div v-else-if="error" class="error-state">
          <p class="error-message">{{ error }}</p>
          <button @click="retryLoad" class="btn btn-secondary">
            Try Again
          </button>
        </div>

        <!-- Main Content -->
        <div v-else class="share-content">
          <!-- Create New Share Link -->
          <div v-if="!hasActiveShare" class="create-share">
            <div class="share-info">
              <h3>Create Public Share Link</h3>
              <p>
                Generate a public link that anyone can access to view this
                content's transcript and summary.
              </p>
              <ul class="share-features">
                <li>
                  <i class="icon-check"></i> Public access to transcript and
                  summary
                </li>
                <li><i class="icon-check"></i> Downloads require sign-up</li>
                <li>
                  <i class="icon-check"></i> AI Chat requires authentication
                </li>
              </ul>
            </div>

            <div class="expiration-options">
              <label>
                <input
                  type="checkbox"
                  v-model="useExpiration"
                  class="expiration-checkbox"
                />
                Set expiration date
              </label>
              <div v-if="useExpiration" class="expiration-input">
                <input
                  type="datetime-local"
                  aria-label="Expiration date"
                  v-model="expirationDate"
                  :min="minDate"
                  class="form-input"
                />
              </div>
            </div>

            <button
              @click="createShare"
              class="btn btn-primary"
              :disabled="creatingShare"
            >
              <span v-if="creatingShare" class="loading-dots">Creating...</span>
              <span v-else>Create Share Link</span>
            </button>
          </div>

          <!-- Existing Share Links -->
          <div v-else class="existing-shares">
            <h3>Active Share Links</h3>

            <div
              v-for="share in activeShares"
              :key="share.id"
              class="share-item"
            >
              <div class="share-info">
                <div class="share-url">
                  <input
                    :value="generateShareUrl(share.shareLinkUUID)"
                    readonly
                    class="url-input"
                    :id="`url-${share.id}`"
                  />
                </div>
                <div class="share-meta">
                  <span class="access-count"
                    >{{ share.accessCount }} views</span
                  >
                  <span v-if="share.expiresAt" class="expiration">
                    Expires: {{ formatDate(share.expiresAt) }}
                  </span>
                  <span v-else class="no-expiration">No expiration</span>
                </div>
              </div>

              <div class="share-actions">
                <button
                  @click="copyShareUrl(share.shareLinkUUID, share.id)"
                  class="action-btn copy-btn"
                >
                  <i class="icon-link"></i>
                  {{ copiedShareId === share.id ? "Copied!" : "Copy" }}
                </button>
                <button
                  @click="toggleShareStatus(share)"
                  class="action-btn toggle-btn"
                >
                  <i :class="share.isActive ? 'icon-pause' : 'icon-play'"></i>
                  {{ share.isActive ? "Disable" : "Enable" }}
                </button>
                <button
                  @click="deleteShare(share)"
                  class="action-btn delete-btn"
                >
                  <i class="icon-delete"></i>
                  Delete
                </button>
              </div>
            </div>

            <!-- Create Additional Link -->
            <div class="add-link">
              <button @click="showCreateForm = true" class="btn btn-secondary">
                <i class="icon-plus"></i> Create Another Link
              </button>
            </div>

            <!-- Create Form (when creating additional) -->
            <div v-if="showCreateForm" class="create-additional">
              <div class="expiration-options">
                <label>
                  <input
                    type="checkbox"
                    v-model="useExpiration"
                    class="expiration-checkbox"
                  />
                  Set expiration date
                </label>
                <div v-if="useExpiration" class="expiration-input">
                  <input
                    type="datetime-local"
                    aria-label="Expiration date"
                    v-model="expirationDate"
                    :min="minDate"
                    class="form-input"
                  />
                </div>
              </div>

              <div class="form-actions">
                <button
                  @click="createShare"
                  class="btn btn-primary"
                  :disabled="creatingShare"
                >
                  <span v-if="creatingShare" class="loading-dots"
                    >Creating...</span
                  >
                  <span v-else>Create Link</span>
                </button>
                <button @click="cancelCreate" class="btn btn-secondary">
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { useAppStore } from "@/stores/app-store";
import { useWorkSpaceStore } from "@/stores/workspace-store";
import { mapStores } from "pinia";
import {
  createShareLink,
  getShareLinks,
  updateShareLink,
  deleteShareLink,
  generateShareUrl,
  copyShareUrl,
} from "@/services/share.service.js";
import { analytics } from "@/services/analytics.service";

export default {
  name: "SharePopup",
  props: ["showSharePopup"],
  data() {
    return {
      loading: false,
      loadingMessage: "",
      error: null,
      shares: [],
      creatingShare: false,
      showCreateForm: false,
      useExpiration: false,
      expirationDate: "",
      copiedShareId: null,
    };
  },
  computed: {
    ...mapStores(useAppStore),
    ...mapStores(useWorkSpaceStore),
    getMediaTitle() {
      const audioFile = this.workspaceStore.activeAudioFile;
      if (audioFile && audioFile.userDefinedName) {
        return audioFile.userDefinedName.split(".")[0];
      }
      return "Media File";
    },
    hasActiveShare() {
      return this.activeShares.length > 0;
    },
    activeShares() {
      return this.shares.filter((share) => share.isActive);
    },
    minDate() {
      const now = new Date();
      now.setMinutes(now.getMinutes() - now.getTimezoneOffset());
      return now.toISOString().slice(0, 16);
    },
  },
  watch: {
    showSharePopup(newVal) {
      if (newVal) {
        const audioFileUUID = this.$route.params.audioFileUUID;
        analytics.sharePopupOpened({
          audio_file_uuid: audioFileUUID,
          has_existing_share: this.hasActiveShare,
        });
        this.loadShares();
      }
    },
  },
  methods: {
    closeSharePopup() {
      analytics.sharePopupClosed();
      this.appStore.showSharePopup = false;
      this.resetForm();
    },

    resetForm() {
      this.showCreateForm = false;
      this.useExpiration = false;
      this.expirationDate = "";
      this.error = null;
      this.copiedShareId = null;
    },

    async loadShares() {
      try {
        this.loading = true;
        this.loadingMessage = "Loading existing shares...";
        this.error = null;

        const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
        const audioFileUUID = this.$route.params.audioFileUUID;

        const result = await getShareLinks(workspaceUUID, audioFileUUID);

        if (result.error) {
          this.error = result.error.message || "Failed to load shares";
        } else {
          this.shares = result.data || [];
        }
      } catch (err) {
        console.error("Error loading shares:", err);
        this.error = "Failed to load shares. Please try again.";
      } finally {
        this.loading = false;
      }
    },

    async createShare() {
      try {
        this.creatingShare = true;
        this.error = null;

        const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
        const audioFileUUID = this.$route.params.audioFileUUID;

        analytics.shareCreateClicked({
          audio_file_uuid: audioFileUUID,
        });

        const options = {};
        if (this.useExpiration && this.expirationDate) {
          options.expiresAt = new Date(this.expirationDate).toISOString();
        }

        const result = await createShareLink(
          workspaceUUID,
          audioFileUUID,
          options,
        );

        if (result.error) {
          this.error = result.error.message || "Failed to create share link";
          analytics.errorShareCreateFailed({
            audio_file_uuid: audioFileUUID,
            error_message: result.error.message,
          });
        } else {
          // Add new share to the list
          this.shares.unshift(result.data);

          analytics.shareLinkCreated({
            audio_file_uuid: audioFileUUID,
            share_link_uuid: result.data.shareLinkUUID,
            has_expiration: !!this.useExpiration,
          });

          // Auto-copy the first share link
          if (this.shares.length === 1) {
            await this.copyShareUrl(result.data.shareLinkUUID, result.data.id);
          }

          // Reset form
          this.resetForm();

          this.$toast?.success("Share link created successfully!");
        }
      } catch (err) {
        console.error("Error creating share:", err);
        this.error = "Failed to create share link. Please try again.";
      } finally {
        this.creatingShare = false;
      }
    },

    async copyShareUrl(shareLinkUUID, shareId) {
      try {
        const result = await copyShareUrl(shareLinkUUID);

        if (result.success) {
          this.copiedShareId = shareId;
          const audioFileUUID = this.$route.params.audioFileUUID;
          analytics.shareLinkCopied({
            audio_file_uuid: audioFileUUID,
            share_link_uuid: shareLinkUUID,
          });
          this.$toast?.success("Share link copied to clipboard!");

          // Reset copied state after 2 seconds
          setTimeout(() => {
            this.copiedShareId = null;
          }, 2000);
        }
      } catch (err) {
        console.error("Error copying share URL:", err);
        this.$toast?.error("Failed to copy link to clipboard");
      }
    },

    async toggleShareStatus(share) {
      try {
        const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
        const audioFileUUID = this.$route.params.audioFileUUID;

        const result = await updateShareLink(
          workspaceUUID,
          audioFileUUID,
          share.shareLinkUUID,
          { isActive: !share.isActive },
        );

        if (result.error) {
          this.$toast?.error(result.error.message || "Failed to update share");
        } else {
          // Update local share object
          const index = this.shares.findIndex((s) => s.id === share.id);
          if (index !== -1) {
            this.shares[index] = { ...this.shares[index], ...result.data };
          }

          // Track deactivation specifically
          if (share.isActive) {
            analytics.shareLinkDeactivated({
              audio_file_uuid: audioFileUUID,
              share_link_uuid: share.shareLinkUUID,
            });
          }

          const action = share.isActive ? "disabled" : "enabled";
          this.$toast?.success(`Share link ${action} successfully!`);
        }
      } catch (err) {
        console.error("Error toggling share status:", err);
        this.$toast?.error("Failed to update share status");
      }
    },

    async deleteShare(share) {
      if (
        !confirm(
          "Are you sure you want to delete this share link? This action cannot be undone.",
        )
      ) {
        return;
      }

      try {
        const workspaceUUID = this.workspaceStore.activeWorkspaceUUID;
        const audioFileUUID = this.$route.params.audioFileUUID;

        const result = await deleteShareLink(
          workspaceUUID,
          audioFileUUID,
          share.shareLinkUUID,
        );

        if (result.error) {
          this.$toast?.error(result.error.message || "Failed to delete share");
        } else {
          // Remove from local shares array
          const index = this.shares.findIndex((s) => s.id === share.id);
          if (index !== -1) {
            this.shares.splice(index, 1);
          }

          this.$toast?.success("Share link deleted successfully!");
        }
      } catch (err) {
        console.error("Error deleting share:", err);
        this.$toast?.error("Failed to delete share link");
      }
    },

    generateShareUrl(shareLinkUUID) {
      return generateShareUrl(shareLinkUUID);
    },

    formatDate(dateString) {
      const date = new Date(dateString);
      return (
        date.toLocaleDateString() +
        " " +
        date.toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" })
      );
    },

    cancelCreate() {
      this.showCreateForm = false;
      this.useExpiration = false;
      this.expirationDate = "";
    },

    retryLoad() {
      this.loadShares();
    },
  },
};
</script>

<style scoped>
/* Loading and Error States */
.loading-state,
.error-state {
  text-align: center;
  padding: 2rem 1rem;
}

.loading-spinner {
  width: 40px;
  height: 40px;
  border: 4px solid #f3f3f3;
  border-top: 4px solid #007bff;
  border-radius: 50%;
  animation: spin 1s linear infinite;
  margin: 0 auto 1rem;
}

@keyframes spin {
  0% {
    transform: rotate(0deg);
  }
  100% {
    transform: rotate(360deg);
  }
}

.error-message {
  color: #dc3545;
  margin-bottom: 1rem;
}

/* Share Content */
.share-content {
  max-height: 70vh;
  overflow-y: auto;
}

/* Create Share Section */
.create-share {
  padding: 1rem 0;
}

.share-info h3 {
  margin: 0 0 0.5rem 0;
  color: #333;
  font-size: 1.1rem;
}

.share-info p {
  margin: 0 0 1rem 0;
  color: #666;
  line-height: 1.5;
}

.share-features {
  list-style: none;
  padding: 0;
  margin: 0 0 1.5rem 0;
}

.share-features li {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.5rem;
  color: #555;
  font-size: 0.9rem;
}

.share-features i {
  color: #28a745;
  font-size: 0.8rem;
}

/* Expiration Options */
.expiration-options {
  margin-bottom: 1.5rem;
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 6px;
}

.expiration-options label {
  display: flex;
  align-items: center;
  gap: 0.5rem;
  margin-bottom: 0.75rem;
  cursor: pointer;
  font-size: 0.9rem;
}

.expiration-checkbox {
  margin: 0;
}

.expiration-input {
  margin-left: 1.5rem;
}

.form-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 0.9rem;
}

/* Existing Shares */
.existing-shares h3 {
  margin: 0 0 1rem 0;
  color: #333;
  font-size: 1.1rem;
}

.share-item {
  border: 1px solid #e9ecef;
  border-radius: 6px;
  padding: 1rem;
  margin-bottom: 1rem;
  background-color: #fff;
}

.share-info {
  margin-bottom: 1rem;
}

.share-url {
  margin-bottom: 0.5rem;
}

.url-input {
  width: 100%;
  padding: 0.5rem;
  border: 1px solid #ddd;
  border-radius: 4px;
  background-color: #f8f9fa;
  font-size: 0.85rem;
  color: #666;
}

.share-meta {
  display: flex;
  gap: 1rem;
  font-size: 0.8rem;
  color: #666;
}

.access-count {
  font-weight: 500;
}

.expiration {
  color: #dc3545;
}

.no-expiration {
  color: #28a745;
}

/* Share Actions */
.share-actions {
  display: flex;
  gap: 0.5rem;
  flex-wrap: wrap;
}

.action-btn {
  display: flex;
  align-items: center;
  gap: 0.25rem;
  padding: 0.4rem 0.8rem;
  border: 1px solid #dee2e6;
  border-radius: 4px;
  background-color: #fff;
  color: #495057;
  font-size: 0.8rem;
  cursor: pointer;
  transition: all 0.2s;
}

.action-btn:hover {
  background-color: #f8f9fa;
  border-color: #adb5bd;
}

.copy-btn:hover {
  background-color: #e7f3ff;
  border-color: #007bff;
  color: #007bff;
}

.toggle-btn:hover {
  background-color: #fff3cd;
  border-color: #ffc107;
  color: #856404;
}

.delete-btn:hover {
  background-color: #f8d7da;
  border-color: #dc3545;
  color: #dc3545;
}

/* Add Link Section */
.add-link {
  text-align: center;
  padding: 1rem 0;
  border-top: 1px solid #e9ecef;
  margin-top: 1rem;
}

/* Create Additional Form */
.create-additional {
  padding: 1rem;
  background-color: #f8f9fa;
  border-radius: 6px;
  margin-top: 1rem;
}

.form-actions {
  display: flex;
  gap: 0.5rem;
  margin-top: 1rem;
}

/* Buttons */
.btn {
  display: inline-flex;
  align-items: center;
  gap: 0.5rem;
  padding: 0.6rem 1.2rem;
  border: none;
  border-radius: 4px;
  font-size: 0.9rem;
  font-weight: 500;
  cursor: pointer;
  text-decoration: none;
  transition: all 0.2s;
}

.btn:disabled {
  opacity: 0.6;
  cursor: not-allowed;
}

.btn-primary {
  background-color: #f16622;
  color: white;
}

.btn-primary:hover:not(:disabled) {
  background-color: #d9551a;
  transform: translateY(-1px);
}

.btn-secondary {
  background-color: transparent;
  color: #007bff;
  border: 1px solid #007bff;
}

.btn-secondary:hover:not(:disabled) {
  background-color: #007bff;
  color: white;
}

/* Loading Animation */
.loading-dots {
  position: relative;
}

.loading-dots::after {
  content: "...";
  animation: dots 1.5s steps(4, end) infinite;
}

@keyframes dots {
  0%,
  20% {
    content: ".";
  }
  40% {
    content: "..";
  }
  60% {
    content: "...";
  }
  80%,
  100% {
    content: "";
  }
}

/* Icon placeholders */
.icon-check::before {
  content: "✓";
}
.icon-link::before {
  content: "🔗";
}
.icon-plus::before {
  content: "+";
}
.icon-pause::before {
  content: "⏸";
}
.icon-play::before {
  content: "▶";
}
.icon-delete::before {
  content: "🗑";
}

/* Responsive Design */
@media (max-width: 480px) {
  .share-meta {
    flex-direction: column;
    gap: 0.25rem;
  }

  .share-actions {
    flex-direction: column;
  }

  .action-btn {
    justify-content: center;
  }

  .form-actions {
    flex-direction: column;
  }
}
</style>
